import { useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper,
    Checkbox, FormControlLabel, Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import PercentInput from '../PercentInput';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { LEAVE_BLANK, BLANK_QUOTE_RATES, BlankQuoteNotice, applyLeaveBlank, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

// Individual modules — each has its own deductible list in appConfig.
// BWHO is modular: WMI is the required base module, the rest are independently selectable.
const MODULES = [
    { key: 'wmi', label: 'WMI', fullLabel: 'Worldwide Medical Insurance',     appKey: 'wmi', deductibleField: 'wmi_deductible' },
    { key: 'wmp', label: 'WMP', fullLabel: 'Worldwide Medical Plus',          appKey: 'wmp', deductibleField: 'wmp_deductible' },
    { key: 'wme', label: 'WME', fullLabel: 'Worldwide Medicines & Equipment', appKey: 'wme', deductibleField: 'wme_deductible' },
    { key: 'ww',  label: 'WW',  fullLabel: 'Worldwide Wellbeing',             appKey: 'ww',  deductibleField: 'ww_deductible' },
];

const CURRENCIES = ['USD', 'EURO', 'GBP', 'SGD', 'AUD'];
const PAYMENT_FREQUENCIES = ['Annually', 'Semi Annually', 'Quarterly', 'Monthly'];

function getCurrencySign(currency) {
    if (currency === 'USD') return '$';
    if (currency === 'SGD') return 'S$';
    if (currency === 'EURO') return '€';
    if (currency === 'GBP') return '£';
    return '';
}

function formatRate(rate, currency) {
    if (rate === null || rate === undefined) return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return getCurrencySign(currency) + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Same as Lifeline: discount/loading scale the module rates only, not the assistance covers
function getRateFactor(discount, loading) {
    return (100 - (parseFloat(discount) || 0) + (parseFloat(loading) || 0)) / 100;
}

export default function BWHO_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const areaOptions = appConfig?.content?.set2_area_of_coverage || ['Worldwide excl USA'];
    const defaultArea = areaOptions.includes('Worldwide excl USA') ? 'Worldwide excl USA' : (areaOptions[0] || 'Worldwide excl USA');
    const getModuleDeductibles = (moduleAppKey, curr) =>
        appConfig?.content?.deductibles?.bwho?.[moduleAppKey]?.[curr] || [];

    // WMI is the required base module; the rest are independently toggleable
    const [selectedModules, setSelectedModules] = useState({ wmi: true, wmp: false, wme: false, ww: false });
    const [areaOfCoverage, setAreaOfCoverage] = useState(defaultArea);
    const [currency, setCurrency] = useState('USD');
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [includeEvacuation, setIncludeEvacuation] = useState(false);
    const [discount, setDiscount] = useState('0');
    const [loadingPercent, setLoadingPercent] = useState('0');
    // { [quoteName]: { wmi: '', wmp: '', wme: '', ww: '' } }
    const [quoteDeductibles, setQuoteDeductibles] = useState({});
    // { [quoteName]: { [moduleKey]: true } }
    const [deductibleErrors, setDeductibleErrors] = useState({});
    const [ratesData, setRatesData] = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    const [loading, setLoading] = useState(false);
    const initializingFromEdit = useRef(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    const activeModules = MODULES.filter(m => selectedModules[m.key]);
    const selectedPlanKey = activeModules.map(m => m.key).join('_');
    const selectedPlanId = `bwho_${selectedPlanKey}`;
    const selectedPlanLabel = activeModules.map(m => m.label).join(' + ');

    // Pre-populate form when opening in edit mode
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            initializingFromEdit.current = true;
            const editingKeys = (editingPlan.plan_id || '').replace(/^bwho_/, '').split('_').filter(Boolean);
            setSelectedModules({
                wmi: true,
                wmp: editingKeys.includes('wmp'),
                wme: editingKeys.includes('wme'),
                ww: editingKeys.includes('ww'),
            });
            setAreaOfCoverage(editingPlan.payloads?.area_of_coverage || defaultArea);
            setCurrency(editingPlan.payloads?.currency || 'USD');
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            const savedDiscount = editingPlan.payloads?.discount ?? '0';
            const savedLoading = editingPlan.payloads?.loading ?? '0';
            setDiscount(String(savedDiscount));
            setLoadingPercent(String(savedLoading));
            const savedFactor = getRateFactor(savedDiscount, savedLoading);
            // Undo the saved discount/loading so ratesData holds the raw rate again
            const unscale = val => (val != null && savedFactor > 0 ? val / savedFactor : val);
            const deductibles = {};
            (quotes || []).forEach(quote => {
                // deductibles_per_quote[uid] is now { wmi, wmp, wme, ww }
                deductibles[quote.name] = editingPlan.deductibles_per_quote?.[quote.uid] || {};
            });
            setQuoteDeductibles(deductibles);

            // Reconstruct ratesData from stored premiums including individual module rates
            const ratesMap = {};
            let anyEvacuation = false;
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                // Match the specific plan instance (plan_uid) so editing one of several same-tier
                // plans doesn't pull rates from a sibling; fall back to plan_id for plans saved
                // before per-instance uids existed.
                const matchesEditingPlan = p => editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id;
                // Evacuation was baked into each client's own premium_rate on save
                const evacuations = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(matchesEditingPlan);
                    return premium?.evacuation_premium ?? null;
                });
                if (evacuations.some(e => e != null)) anyEvacuation = true;
                const rawRates = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(matchesEditingPlan);
                    return {
                        wmi: unscale(premium?.wmi_premium ?? null),
                        wmp: unscale(premium?.wmp_premium ?? null),
                        wme: unscale(premium?.wme_premium ?? null),
                        ww:  unscale(premium?.ww_premium  ?? null),
                    };
                });
                ratesMap[quote.uid] = { rawRates, evacuations };
            });
            setIncludeEvacuation(anyEvacuation);
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
            // Clear the init flag AFTER all dependent effects (currency, selectedModules) have fired
            setTimeout(() => { initializingFromEdit.current = false; }, 0);
        } else if (open) {
            setIncludeEvacuation(false);
            setDiscount('0');
            setLoadingPercent('0');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Reset deductibles to defaults when currency changes (skip during edit initialization)
    useEffect(() => {
        if (initializingFromEdit.current) return;
        const defaults = {};
        (quotes || []).forEach(q => {
            const moduleDefaults = {};
            MODULES.forEach(m => {
                moduleDefaults[m.key] = getModuleDeductibles(m.appKey, currency)[0] || '';
            });
            defaults[q.name] = moduleDefaults;
        });
        setQuoteDeductibles(defaults);
        invalidateRates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currency]);

    // When selected modules change: fill in defaults for any module not yet set.
    // Rates are kept — the API returns every module's rate in one fetch, so the table just
    // shows a different set of rows.
    useEffect(() => {
        if (initializingFromEdit.current) return;
        setQuoteDeductibles(prev => {
            const next = { ...prev };
            (quotes || []).forEach(q => {
                const existing = { ...(prev[q.name] || {}) };
                MODULES.forEach(m => {
                    if (!existing[m.key]) {
                        existing[m.key] = getModuleDeductibles(m.appKey, currency)[0] || '';
                    }
                });
                next[q.name] = existing;
            });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedModules]);

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return quoteDeductibles[quote.name]?.wmi === LEAVE_BLANK;
    }

    async function handleViewRates() {
        // Validate: each active module for each quote must have a deductible
        const errors = {};
        (quotes || []).forEach(q => {
            if (isBlankQuote(q)) return;
            activeModules.forEach(m => {
                if (!quoteDeductibles[q.name]?.[m.key]) {
                    if (!errors[q.name]) errors[q.name] = {};
                    errors[q.name][m.key] = true;
                }
            });
        });
        if (Object.keys(errors).length > 0) {
            setDeductibleErrors(errors);
            const firstQuote = (quotes || []).find(q => errors[q.name]);
            const firstModule = activeModules.find(m => errors[firstQuote?.name]?.[m.key]);
            toast.error(`Please select a deductible for "${firstQuote?.name}" — ${firstModule?.label}`, { hideProgressBar: true, autoClose: 3000 });
            return;
        }
        setDeductibleErrors({});
        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            deductibles: {
                                bwho: {
                                    wmi_deductible: quoteDeductibles[quote.name]?.wmi || '',
                                    wmp_deductible: quoteDeductibles[quote.name]?.wmp || '',
                                    wme_deductible: quoteDeductibles[quote.name]?.wme || '',
                                    ww_deductible:  quoteDeductibles[quote.name]?.ww  || '',
                                },
                            },
                            insurers: ['bwho'],
                            specifics: {},
                            specific_area_of_coverage: { bwho: areaOfCoverage },
                            rates_version_per_insurer: { bwho: selectedRatesVersion?.bwho || '' },
                            specific_currency: { bwho: currency },
                            specific_payment_frequency: { bwho: paymentFrequency },
                        },
                    }).then(response => ({ quoteUid: quote.uid, data: response?.data?.bwho }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quoteUid, data, blank }) => {
                if (blank) {
                    ratesMap[quoteUid] = BLANK_QUOTE_RATES;
                    return;
                }
                if (data?.rates) {
                    // Raw individual component rates from API
                    const rawRates = data.rates.map(r => ({
                        wmi: r.wmi,
                        wmp: r.wmp,
                        wme: r.wme,
                        ww:  r.ww,
                    }));
                    // assistance_covers is index-aligned with rates (one entry per client)
                    const evacuations = (data.assistance_covers || []).map(ac => ac?.evacuation ?? null);
                    ratesMap[quoteUid] = { rawRates, evacuations };
                } else {
                    ratesMap[quoteUid] = data;
                }
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching BWHO rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    const rateFactor = getRateFactor(discount, loadingPercent);

    // Cumulative rate for the selected modules (sum of each active module's rate).
    // ratesData keeps the raw API rates; discount/loading are applied on read.
    function getRate(clientIndex, quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        let total = 0;
        let hasAny = false;
        activeModules.forEach(m => {
            const val = getRawRate(clientIndex, m.key, quoteUid);
            if (typeof val === 'number') { total += val; hasAny = true; }
        });
        return hasAny ? total : null;
    }

    function getSubtotal(quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getRate(i, quoteUid);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    // Evacuation assistance cover amount for a specific client on a quote
    function getEvacuation(clientIndex, quoteUid) {
        return ratesData?.[quoteUid]?.evacuations?.[clientIndex] ?? null;
    }

    function getEvacuationSubtotal(quoteUid) {
        if (!ratesData?.[quoteUid]?.evacuations) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const val = ratesData[quoteUid].evacuations[i];
            if (typeof val === 'number') { total += val; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    // Individual module rate (e.g. just wmp component, not cumulative), with discount/loading applied
    function getRawRate(clientIndex, moduleKey, quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        const rate = ratesData[quoteUid].rawRates[clientIndex]?.[moduleKey];
        return typeof rate === 'number' ? rate * rateFactor : rate;
    }

    function getRawSubtotal(moduleKey, quoteUid) {
        if (!ratesData?.[quoteUid]?.rawRates) return null;
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getRawRate(i, moduleKey, quoteUid);
            if (typeof rate === 'number') { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    // When WMI deductible changes, cascade to other active modules:
    // - Zero Deductible for WMI → all others get their first option (Zero)
    // - Non-zero WMI → all others get their second option if available, else first
    function handleWmiDeductibleChange(quoteName, newWmiValue) {
        const wmiOptions = getModuleDeductibles('wmi', currency);
        const isZero = wmiOptions.indexOf(newWmiValue) === 0;
        setQuoteDeductibles(prev => {
            const updated = { ...(prev[quoteName] || {}), wmi: newWmiValue };
            MODULES.forEach(m => {
                if (m.key === 'wmi') return;
                const options = getModuleDeductibles(m.appKey, currency);
                updated[m.key] = isZero ? (options[0] || '') : (options[1] || options[0] || '');
            });
            return { ...prev, [quoteName]: updated };
        });
        setDeductibleErrors(prev => ({ ...prev, [quoteName]: {} }));
        invalidateRates();
    }

    function buildBwhoDeductibleString(quoteName) {
        const wmiVal = quoteDeductibles[quoteName]?.wmi || '';
        const isZero = wmiVal.toLowerCase().includes('zero') || wmiVal.toLowerCase().includes('nil');
        if (isZero) return 'Zero Deductible';
        return activeModules
            .map((m, i) => {
                const val = quoteDeductibles[quoteName]?.[m.key] || '';
                return i === 0 ? `${val}` : `${m.label} ${val}`;
            })
            .join(', ');
    }

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;

        const activeModuleKeys = activeModules.map(m => m.key);
        // plan_id identifies the module combination and is shared by every instance of it; uid
        // identifies this specific plan instance so multiple plans of the same combination can
        // coexist instead of overwriting each other. Editing reuses the existing plan's uid.
        const planUid = editingPlan ? editingPlan.uid : uuidv4();
        const updates = [];
        (quotes || []).forEach(quote => {
            clients.forEach((client, clientIndex) => {
                const moduleRate = key => getRawRate(clientIndex, key, quote.uid) ?? null;
                const rate = getRate(clientIndex, quote.uid);
                // Evacuation is quoted per-client — bake each client's own amount into their premium.
                const evacuation = includeEvacuation ? getEvacuation(clientIndex, quote.uid) : null;
                const applyEvacuation = typeof rate === 'number' && typeof evacuation === 'number';
                const premiumRate = applyEvacuation ? rate + evacuation : (rate ?? null);
                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId: selectedPlanId,
                    planUid,
                    premium: {
                        insurer: 'bwho',
                        plan: selectedPlanKey,
                        plan_label: selectedPlanLabel,
                        included_benefits: '',
                        deductible: buildBwhoDeductibleString(quote.name),
                        area_of_coverage: areaOfCoverage,
                        currency,
                        payment_frequency: paymentFrequency,
                        discount,
                        loading: loadingPercent,
                        premium_rate: premiumRate,
                        // Only persist the premium for modules the user actually selected —
                        // the API returns a rate for every module regardless of selection.
                        // Stored with discount/loading applied, matching premium_rate.
                        wmi_premium: activeModuleKeys.includes('wmi') ? moduleRate('wmi') : null,
                        wmp_premium: activeModuleKeys.includes('wmp') ? moduleRate('wmp') : null,
                        wme_premium: activeModuleKeys.includes('wme') ? moduleRate('wme') : null,
                        ww_premium:  activeModuleKeys.includes('ww')  ? moduleRate('ww')  : null,
                        evacuation_premium: applyEvacuation ? evacuation : null,
                    },
                });
            });
        });

        const plan = {
            plan_id: selectedPlanId,
            uid: planUid,
            plan: selectedPlanKey,
            insurer: 'bwho',
            label: `BUPA WHO ${selectedPlanLabel}`,
            payloads: {
                area_of_coverage: areaOfCoverage,
                currency,
                payment_frequency: paymentFrequency,
                discount,
                loading: loadingPercent,
            },
            deductibles_per_quote: Object.fromEntries(
                (quotes || []).map(quote => [quote.uid, quoteDeductibles[quote.name] || {}])
            ),
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#0c1b4e', px: 2, py: 1 }}>
                <div style={{
                    color: '#fff',
                    backgroundColor: '#0369ae',
                    width: 100,
                    height: 60,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 13,
                    lineHeight: 1.2,
                }}>Worldwide Health Options</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <img src={`${process.env.PUBLIC_URL}/images/Raffles Health Insurance.png`} alt="Raffles Health Insurance" style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <img src={`${process.env.PUBLIC_URL}/images/BUPA Blue Logo.png`} alt="BUPA" style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none' }} draggable={false} onContextMenu={(e) => e.preventDefault()} />
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                <Box>
                {/* Select Plan — modular: WMI is the required base, the rest are independently selectable */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                        {MODULES.map(module => (
                            <FormControlLabel
                                key={module.key}
                                sx={{ mb: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={!!selectedModules[module.key]}
                                        disabled={loading || !!editingPlan}
                                        onChange={(e) => {
                                            // WMI is the required base module — read-only, always checked
                                            if (module.key === 'wmi') return;
                                            const checked = e.target.checked;
                                            setSelectedModules(prev => ({ ...prev, [module.key]: checked }));
                                        }}
                                    />
                                }
                                label={module.fullLabel}
                            />
                        ))}
                    </Box>
                </Box>

                {/* Assistance Cover */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 0.5, fontSize: 15 }}>Assistance Cover</Typography>
                    <FormControlLabel
                        sx={{ mb: 0, whiteSpace: 'nowrap', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                        control={
                            <Checkbox
                                size="small"
                                checked={includeEvacuation}
                                disabled={loading}
                                onChange={(e) => setIncludeEvacuation(e.target.checked)}
                            />
                        }
                        label="Worldwide Evacuation"
                    />
                </Box>
                </Box>

                {/* Options — label on the left, select on the right */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 160px', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Area Of Coverage</Typography>
                        <FormControl size="small" sx={{ width: 160 }}>
                            <Select
                                value={areaOfCoverage}
                                disabled={loading}
                                onChange={(e) => { setAreaOfCoverage(e.target.value); invalidateRates(); }}
                                sx={{ fontSize: 12, height: 32 }}
                            >
                                {areaOptions.map(area => (
                                    <MenuItem key={area} value={area} sx={{ fontSize: 12 }}>{area}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Currency</Typography>
                        <FormControl size="small" sx={{ width: 160 }}>
                            <Select
                                value={currency}
                                disabled={loading}
                                onChange={(e) => setCurrency(e.target.value)}
                                sx={{ fontSize: 12, height: 32 }}
                            >
                                {CURRENCIES.map(c => (
                                    <MenuItem key={c} value={c} sx={{ fontSize: 12 }}>{c}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 160 }}>
                            <Select
                                value={paymentFrequency}
                                disabled={loading}
                                onChange={(e) => { setPaymentFrequency(e.target.value); invalidateRates(); }}
                                sx={{ fontSize: 12, height: 32 }}
                            >
                                {PAYMENT_FREQUENCIES.map(f => (
                                    <MenuItem key={f} value={f} sx={{ fontSize: 12 }}>{f}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                </Box>

                {/* Renewal/Further Discount & Loading */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Discount</Typography>
                        <PercentInput
                            value={discount}
                            onCommit={setDiscount}
                            disabled={loading}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Loading</Typography>
                        <PercentInput
                            value={loadingPercent}
                            onCommit={setLoadingPercent}
                            disabled={loading}
                        />
                </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                {/* Per-Quote Deductibles — one deductible per active module per quote */}
                <Box sx={{ mb: 2 }}>
                    <Typography fontWeight={600} sx={{ mb: 1.5, fontSize: 15 }}>Deductibles per Quote</Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {(quotes || []).map(quote => (
                            <Box key={quote.name}>
                                <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.75 }}>{quote.name}</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, pl: 1 }}>
                                    {activeModules.filter(module => module.key === 'wmi' || !isBlankQuote(quote)).map(module => {
                                        const options = getModuleDeductibles(module.appKey, currency);
                                        return (
                                            <Box key={module.key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Typography sx={{ minWidth: 24, fontSize: 13, color: 'text.secondary' }}>
                                                    {module.label}
                                                </Typography>
                                                <CustomSelect2
                                                    dense
                                                    value={quoteDeductibles[quote.name]?.[module.key] || ''}
                                                    onChange={(e) => {
                                                        const newValue = e.target.value;
                                                        if (module.key === 'wmi') {
                                                            handleWmiDeductibleChange(quote.name, newValue);
                                                        } else {
                                                            setQuoteDeductibles(prev => ({
                                                                ...prev,
                                                                [quote.name]: {
                                                                    ...(prev[quote.name] || {}),
                                                                    [module.key]: newValue,
                                                                },
                                                            }));
                                                            setDeductibleErrors(prev => ({
                                                                ...prev,
                                                                [quote.name]: {
                                                                    ...(prev[quote.name] || {}),
                                                                    [module.key]: false,
                                                                },
                                                            }));
                                                            invalidateRates();
                                                        }
                                                    }}
                                                    items={module.key === 'wmi' ? [LEAVE_BLANK, ...options] : options}
                                                    disabled={loading || module.key !== 'wmi'}
                                                    error={!!deductibleErrors[quote.name]?.[module.key]}
                                                    sx={{ minWidth: 170 }}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {/* Rates Display */}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: '#673ab7' }} />
                    </Box>
                )}

                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {(quotes || []).map(quote => {
                            const deductibleSummary = isBlankQuote(quote) ? LEAVE_BLANK : activeModules
                                .map(m => `${m.label}: ${quoteDeductibles[quote.name]?.[m.key] || '-'}`)
                                .join(' | ');
                            return (
                                <Box key={quote.name} sx={{ mb: 3 }}>
                                    <Typography fontWeight={600} sx={{ mb: 1 }}>
                                        {quote.name}
                                        <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 13 }}>
                                            — {deductibleSummary}
                                        </Typography>
                                    </Typography>
                                    {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ backgroundColor: '#0c1b4e' }}>
                                                    <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 200 }}>Plan</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                            <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                            <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>Subtotal</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {/* One row per active module showing individual component rate */}
                                                {activeModules.map(module => (
                                                    <TableRow key={module.key} sx={{ backgroundColor: '#fff' }}>
                                                        <TableCell sx={{ fontWeight: 500 }}>{module.label}</TableCell>
                                                        {clients.map((client, idx) => (
                                                            <TableCell key={client.uid || idx}>
                                                                {formatRate(getRawRate(idx, module.key, quote.uid), currency)}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell sx={{ fontWeight: 500 }}>
                                                            {formatRate(getRawSubtotal(module.key, quote.uid), currency)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {/* Plan total row (cumulative) — only shown when multiple modules, hidden when the
                                                    combined "+ Evacuation" row below already covers the plan total */}
                                                {activeModules.length > 1 && !(includeEvacuation && getEvacuationSubtotal(quote.uid) != null) && (
                                                    <TableRow sx={{ backgroundColor: '#e8eaf6' }}>
                                                        <TableCell sx={{ fontWeight: 700 }}>{selectedPlanLabel}</TableCell>
                                                        {clients.map((client, idx) => (
                                                            <TableCell key={client.uid || idx} sx={{ fontWeight: 700 }}>
                                                                {formatRate(getRate(idx, quote.uid), currency)}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell sx={{ fontWeight: 700 }}>
                                                            {formatRate(getSubtotal(quote.uid), currency)}
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                                {/* Evacuation assistance cover — quoted per client */}
                                                {includeEvacuation && getEvacuationSubtotal(quote.uid) != null && (
                                                    <>
                                                        <TableRow sx={{ backgroundColor: '#fff' }}>
                                                            <TableCell>Evacuation</TableCell>
                                                            {clients.map((client, idx) => (
                                                                <TableCell key={client.uid || idx}>
                                                                    {formatRate(getEvacuation(idx, quote.uid), currency)}
                                                                </TableCell>
                                                            ))}
                                                            <TableCell sx={{ fontWeight: 500 }}>
                                                                {formatRate(getEvacuationSubtotal(quote.uid), currency)}
                                                            </TableCell>
                                                        </TableRow>
                                                        <TableRow sx={{ backgroundColor: '#e8eaf6' }}>
                                                            <TableCell sx={{ fontWeight: 700 }}>{selectedPlanLabel} + Evacuation</TableCell>
                                                            {clients.map((client, idx) => {
                                                                const rate = getRate(idx, quote.uid);
                                                                const evacuation = getEvacuation(idx, quote.uid);
                                                                const total = typeof rate === 'number' && typeof evacuation === 'number'
                                                                    ? rate + evacuation
                                                                    : rate;
                                                                return (
                                                                    <TableCell key={client.uid || idx} sx={{ fontWeight: 700 }}>
                                                                        {formatRate(total, currency)}
                                                                    </TableCell>
                                                                );
                                                            })}
                                                            <TableCell sx={{ fontWeight: 700 }}>
                                                                {formatRate(
                                                                    getSubtotal(quote.uid) != null
                                                                        ? getSubtotal(quote.uid) + getEvacuationSubtotal(quote.uid)
                                                                        : null,
                                                                    currency
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    </>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </Paper>
)}
                                </Box>
                            );
                        })}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId="bwho" />

                <RatesVersionSelect
                    insurer="bwho"
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />

                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
